// src/pages/VideoCatchupPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import '../styles/VideoCatchupPage.css';
import { createSlug, findShowBySlug, findEpisodeBySlug } from '../utils/slugify';

// Helpers (keep all existing helper functions)
const parseDateFromTitle = (title) => {
    const dateRegex = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})|(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/;
    const match = title && title.match ? title.match(dateRegex) : null;

    if (match) {
        if (match[1]) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            const fullYear = year < 100 ? 2000 + year : year;
            return new Date(fullYear, month, day);
        } else {
            const year = parseInt(match[4], 10);
            const month = parseInt(match[5], 10) - 1;
            const day = parseInt(match[6], 10);
            return new Date(year, month, day);
        }
    }
    return new Date(1970, 0, 1);
};

const sortEpisodesByDate = (episodes) => {
    return [...episodes].sort((a, b) => {
        const dateA = parseDateFromTitle(a.title);
        const dateB = parseDateFromTitle(b.title);
        return dateB - dateA;
    });
};

const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds == null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const formatViews = (v) => {
    if (!v && v !== 0) return '—';
    const n = typeof v === 'number' ? v : parseInt(String(v).replace(/[^0-9]/g, ''), 10) || 0;
    if (n >= 1e9) return (n / 1e9).toFixed(1).replace('.0','') + 'B views';
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace('.0','') + 'M views';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace('.0','') + 'K views';
    return `${n} view${n !== 1 ? 's' : ''}`;
};

const formatDatePretty = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const VideoCatchupPage = () => {
    const [shows, setShows] = useState([]);
    const [currentShowIndex, setCurrentShowIndex] = useState(0);
    const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [descExpanded, setDescExpanded] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false); // New state to track if data is loaded

    const videoRef = useRef(null);
    const mountedRef = useRef(false);
    const playTimerRef = useRef(null);
    const pendingTimerRef = useRef(null);
    const hasLoadedDataRef = useRef(false); // Ref to prevent multiple loads

    const location = useLocation();
    const navigate = useNavigate();
    const { showSlug, episodeSlug } = useParams();

    const CONFIG = {
        dataSource: '/get_videos.php',
        devOrigin: window.location.origin,
        defaultImage: 'https://a8.asurahosting.com/static/uploads/onoradio/album_art.1733486583.png'
    };

    const resolveMediaUrl = useCallback((url) => {
        if (!url) return '';
        try {
            const parsed = new URL(url);
            return parsed.href;
        } catch (e) {
            const base = CONFIG.devOrigin.replace(/\/$/, '');
            if (url.startsWith('/')) return `${base}${url}`;
            return `${base}/${url}`;
        }
    }, [CONFIG.devOrigin]);

    const handleImgError = useCallback((e) => {
        try {
            e.target.onerror = null;
            e.target.src = CONFIG.defaultImage;
        } catch (err) {
            // ignore
        }
    }, [CONFIG.defaultImage]);

    // Update URL when show or episode changes
    const updateUrl = useCallback((showIndex, episodeIndex) => {
        if (showIndex < 0 || !shows[showIndex]) return;
        
        const show = shows[showIndex];
        const showSlug = createSlug(show.title);
        
        if (episodeIndex >= 0 && show.episodes && show.episodes[episodeIndex]) {
            const episode = show.episodes[episodeIndex];
            const episodeSlug = createSlug(episode.title);
            navigate(`/podcasts/${showSlug}/${episodeSlug}`, { replace: true });
        } else {
            navigate(`/podcasts/${showSlug}`, { replace: true });
        }
    }, [shows, navigate]);

    // Modified loadEpisode to update URL
    const loadEpisode = useCallback((showIndex, episodeIndex, autoplay = true) => {
        if (!shows[showIndex] || !shows[showIndex].episodes || !shows[showIndex].episodes[episodeIndex]) return;
        const show = shows[showIndex];
        const episode = show.episodes[episodeIndex];

        setCurrentShowIndex(showIndex);
        setCurrentEpisodeIndex(episodeIndex);
        setDescExpanded(false);

        // Update URL to reflect current show/episode
        updateUrl(showIndex, episodeIndex);

        if (!videoRef.current) return;

        if (playTimerRef.current) {
            clearTimeout(playTimerRef.current);
            playTimerRef.current = null;
        }

        try {
            videoRef.current.pause();

            const videoUrl = episode.video ? episode.video : '';
            const posterUrl = episode.thumbnail || episode.image || show.image || CONFIG.defaultImage;

            videoRef.current.crossOrigin = 'anonymous';
            videoRef.current.preload = 'metadata';
            videoRef.current.playsInline = true;

            videoRef.current.src = videoUrl;
            videoRef.current.poster = posterUrl;
            videoRef.current.load();
        } catch (err) {
            console.warn('Error setting video src:', err);
            return;
        }

        if (autoplay) {
            playTimerRef.current = setTimeout(() => {
                playTimerRef.current = null;
                if (!mountedRef.current || !videoRef.current) return;

                const playPromise = videoRef.current.play();
                if (playPromise && playPromise.catch) {
                    playPromise.catch(err => {
                        if (!mountedRef.current) return;
                        console.warn('Video play() failed:', err);
                    });
                }
            }, 150);
        }
    }, [shows, updateUrl, CONFIG.defaultImage]);

    const handleSelectShow = useCallback((index) => {
        setCurrentShowIndex(index);
        setCurrentEpisodeIndex(-1);
        updateUrl(index, -1);
    }, [updateUrl]);

    const prevEpisode = useCallback(() => {
        const show = shows[currentShowIndex];
        if (!show?.episodes?.length) return;
        if (currentEpisodeIndex < show.episodes.length - 1) {
            loadEpisode(currentShowIndex, currentEpisodeIndex + 1);
        } else if (currentShowIndex > 0) {
            const newShowIndex = currentShowIndex - 1;
            loadEpisode(newShowIndex, 0);
        }
    }, [currentShowIndex, currentEpisodeIndex, shows, loadEpisode]);

    const nextEpisode = useCallback(() => {
        const show = shows[currentShowIndex];
        if (!show?.episodes?.length) return;
        if (currentEpisodeIndex > 0) {
            loadEpisode(currentShowIndex, currentEpisodeIndex - 1);
        } else if (currentShowIndex < shows.length - 1) {
            const newShowIndex = currentShowIndex + 1;
            loadEpisode(newShowIndex, 0);
        }
    }, [currentShowIndex, currentEpisodeIndex, shows, loadEpisode]);

    // Load shows data only once
    const loadShowsData = useCallback(async () => {
        // Prevent multiple loads
        if (hasLoadedDataRef.current) {
            console.log('[VideoCatchup] Data already loaded, skipping');
            return;
        }
        
        hasLoadedDataRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            console.log('[VideoCatchup] Fetching from:', CONFIG.dataSource);
            
            const response = await fetch(CONFIG.dataSource, {
                headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            });
            
            console.log('[VideoCatchup] Response status:', response.status);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            console.log('[VideoCatchup] Data received:', data);
            
            // Check for error in response
            if (data.error) {
                console.error('[VideoCatchup] Server error:', data.error);
                setError(`Server error: ${data.error}`);
                setIsLoading(false);
                return;
            }
            
            let showsData = data.shows || [];
            console.log('[VideoCatchup] Shows found:', showsData.length);
            
            if (showsData.length === 0) {
                console.warn('[VideoCatchup] No shows found');
                setError('No shows found. Please check if video files exist in the correct directory.');
                setIsLoading(false);
                return;
            }
            
            // Normalize the shows data
            const normalized = showsData.map(show => {
                const safeShow = { ...show };
                if (safeShow.image) safeShow.image = resolveMediaUrl(safeShow.image);
                if (safeShow.episodes && Array.isArray(safeShow.episodes)) {
                    safeShow.episodes = safeShow.episodes.map(ep => ({
                        ...ep,
                        thumbnail: ep.thumbnail ? resolveMediaUrl(ep.thumbnail) : (ep.image ? resolveMediaUrl(ep.image) : ''),
                        image: ep.image ? resolveMediaUrl(ep.image) : '',
                        video: ep.video ? resolveMediaUrl(ep.video) : ''
                    }));
                }
                return safeShow;
            });

            setShows(normalized);
            setIsDataLoaded(true);

            // Determine target show/episode based on URL slugs or query params
            let targetShowIndex = 0;
            let targetEpisodeIndex = -1;
            let shouldAutoplay = false;

            // Check URL params first for slug-based routing
            if (showSlug && normalized.length > 0) {
                const foundShowIndex = findShowBySlug(normalized, showSlug);
                if (foundShowIndex >= 0) {
                    targetShowIndex = foundShowIndex;
                    
                    if (episodeSlug && normalized[foundShowIndex]?.episodes) {
                        const foundEpisodeIndex = findEpisodeBySlug(
                            normalized[foundShowIndex].episodes, 
                            episodeSlug
                        );
                        if (foundEpisodeIndex >= 0) {
                            targetEpisodeIndex = foundEpisodeIndex;
                            shouldAutoplay = true;
                        }
                    }
                }
            } else {
                // Fallback to query parameters
                const params = new URLSearchParams(location.search);
                const showParam = params.get('show');
                const episodeParam = params.get('episode');
                const autoplayParam = params.get('autoplay') === 'true';

                if (showParam && normalized.length > 0) {
                    const foundIndex = normalized.findIndex(show =>
                        show.title.toLowerCase().includes(showParam.toLowerCase()) ||
                        showParam.toLowerCase().includes(show.title.toLowerCase())
                    );
                    if (foundIndex >= 0) targetShowIndex = foundIndex;
                }

                if (episodeParam && normalized[targetShowIndex]?.episodes) {
                    const episodes = normalized[targetShowIndex].episodes;
                    const foundEpisodeIndex = episodes.findIndex(ep =>
                        ep.title.toLowerCase().includes(episodeParam.toLowerCase())
                    );
                    if (foundEpisodeIndex >= 0) {
                        targetEpisodeIndex = foundEpisodeIndex;
                        shouldAutoplay = autoplayParam;
                    }
                }
            }

            setCurrentShowIndex(targetShowIndex);

            // If we have a valid episode, load it after a small delay
            if (targetEpisodeIndex >= 0) {
                if (pendingTimerRef.current) {
                    clearTimeout(pendingTimerRef.current);
                    pendingTimerRef.current = null;
                }
                pendingTimerRef.current = setTimeout(() => {
                    pendingTimerRef.current = null;
                    if (mountedRef.current) {
                        loadEpisode(targetShowIndex, targetEpisodeIndex, shouldAutoplay);
                    }
                }, 150);
            } else {
                // Update URL even without episode
                if (showSlug) {
                    updateUrl(targetShowIndex, -1);
                }
            }

        } catch (err) {
            console.error('[VideoCatchup] Error loading shows:', err);
            setError(`Failed to load shows: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [location.search, showSlug, episodeSlug, updateUrl, loadEpisode, resolveMediaUrl]);

    // Handle video events
    useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Track which episode we've already counted, so re-firing `play`
    // (from pause/resume, seeking, etc.) doesn't inflate the count.
    let countedKey = null;

    const onPlay = () => {
        const show    = shows[currentShowIndex];
        const episode = show?.episodes?.[currentEpisodeIndex];
        if (!show || !episode) return;
        if (!show.folder || !episode.folder) return; // API hasn't been updated yet

        const key = `${show.folder}/${episode.folder}`;
        if (countedKey === key) return;
        countedKey = key;

        fetch('/admin/video_catchup/track_video_play.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                show_folder:    show.folder,
                episode_folder: episode.folder,
            }),
        }).catch(() => { /* silent — analytics must not break playback */ });
    };

    const onEnded          = () => nextEpisode();
    const onError          = () => console.error('Video element error object:', v.error);
    const onLoadedMetadata = () => console.log('Video metadata loaded, duration:', v.duration);
    const onCanPlay        = () => console.log('Video can play');

    v.addEventListener('play', onPlay);
    v.addEventListener('ended', onEnded);
    v.addEventListener('error', onError);
    v.addEventListener('loadedmetadata', onLoadedMetadata);
    v.addEventListener('canplay', onCanPlay);

    return () => {
        v.removeEventListener('play', onPlay);
        v.removeEventListener('ended', onEnded);
        v.removeEventListener('error', onError);
        v.removeEventListener('loadedmetadata', onLoadedMetadata);
        v.removeEventListener('canplay', onCanPlay);
    };
    }, [nextEpisode, shows, currentShowIndex, currentEpisodeIndex]);

    // Mount/unmount tracking
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;

            if (playTimerRef.current) {
                clearTimeout(playTimerRef.current);
                playTimerRef.current = null;
            }
            if (pendingTimerRef.current) {
                clearTimeout(pendingTimerRef.current);
                pendingTimerRef.current = null;
            }

            if (videoRef.current) {
                try {
                    videoRef.current.pause();
                    videoRef.current.removeAttribute('src');
                    videoRef.current.load();
                } catch (e) {
                    // ignore errors during cleanup
                }
            }
        };
    }, []);

    // Load shows data only once on mount
    useEffect(() => {
        loadShowsData();
    }, [loadShowsData]);

    // Memoize current show and episodes to prevent unnecessary re-renders
    const currentShow = useMemo(() => shows[currentShowIndex] || {}, [shows, currentShowIndex]);
    const sortedEpisodes = useMemo(() => {
        return currentShow.episodes ? sortEpisodesByDate(currentShow.episodes) : [];
    }, [currentShow]);
    const activeEpisode = useMemo(() => {
        return currentShow.episodes?.[currentEpisodeIndex] || null;
    }, [currentShow, currentEpisodeIndex]);

    return (
        <div className="pageContainer">
            <section className="news-page-header">
                <h1>Podcasts</h1>
                <p>Watch our video podcasts on demand!</p>
            </section>

            <section className="video-player-widget yt-style">
                <div className="catchup-header">
                    <h2>
                    Select Show
                    </h2>
                </div>
                <div className="yt-layout">
                    <main className="player-main">
                        <div className="video-wrap">
                            <div className="video-aspect">
                                <video
                                    ref={videoRef}
                                    controls
                                    className="video-element"
                                    playsInline
                                    preload="metadata"
                                >
                                    Your browser does not support HTML5 video.
                                </video>
                            </div>
                        </div>

                        <div className="video-meta-block">
                            <h2 className="video-title">
                                {activeEpisode ? activeEpisode.title : (isLoading ? 'Loading...' : 'Select an episode to play')}
                            </h2>
                            <div className="video-stats-row">
                                <div className="left-stats">
                                </div>

                                <div className="right-actions">
                                    <button 
                                        className="icon-btn share"
                                        onClick={() => {
                                            if (activeEpisode) {
                                                const shareUrl = `${window.location.origin}/podcasts/${createSlug(currentShow.title)}/${createSlug(activeEpisode.title)}`;
                                                navigator.clipboard.writeText(shareUrl).then(() => {
                                                    alert('Link copied to clipboard!');
                                                });
                                            }
                                        }}
                                    >
                                        <i className="fas fa-share"></i> Share
                                    </button>
                                </div>
                            </div>

                            <div className="channel-row">
                                <div className="channel-left">
                                    <img
                                        className="channel-avatar"
                                        src={currentShow.image || CONFIG.defaultImage}
                                        alt={currentShow.title}
                                        onError={handleImgError}
                                    />
                                    <div className="channel-info">
                                        <div className="channel-name">
                                            {currentShow.title || 'Unknown Show'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="description-block">
                                <div className={`desc-text ${descExpanded ? 'expanded' : ''}`}>
                                    {activeEpisode ? (activeEpisode.description || currentShow.description || 'No description available.') : 'Select an episode to see the description.'}
                                </div>
                                {activeEpisode && (activeEpisode.description || currentShow.description) && (
                                    <button className="desc-toggle" onClick={() => setDescExpanded(!descExpanded)}>
                                        {descExpanded ? 'Show less' : 'Show more'}
                                    </button>
                                )}
                            </div>

                            <div className="player-controls-row">
                                
                            </div>
                        </div>
                    </main>

                    {/* SIDEBAR: Show selection */}
                    <aside className="suggested-sidebar">
                        <div className="suggested-head">
                            <span>Shows</span>
                        </div>

                        <div className="suggested-list">
                            {isLoading ? (
                                <div className="loading-pill">Loading shows...</div>
                            ) : error ? (
                                <div className="no-content">
                                    <i className="fas fa-exclamation-triangle" />
                                    <p>{error}</p>
                                </div>
                            ) : shows.length === 0 ? (
                                <div className="no-content">
                                    <i className="fas fa-headphones-slash" />
                                    <p>No shows found.</p>
                                </div>
                            ) : (
                                shows.map((show, idx) => {
                                    const showLink = `/podcasts/${createSlug(show.title)}`;
                                    
                                    return (
                                        <Link
                                            key={`show-${idx}-${show.title}`}
                                            to={showLink}
                                            className={`suggested-item ${idx === currentShowIndex ? 'active' : ''}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleSelectShow(idx);
                                            }}
                                        >
                                            <div className="suggested-thumb">
                                                <img 
                                                    src={show.image || CONFIG.defaultImage}
                                                    alt={show.title}
                                                    onError={handleImgError}
                                                />
                                            </div>
                                            <div className="suggested-info">
                                                <div className="suggested-title">{show.title}</div>
                                                <div className="suggested-meta">
                                                    {show.episodes ? `${show.episodes.length} episodes` : ''}
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })
                            )}
                        </div>
                    </aside>
                </div>

                {/* BOTTOM SECTION: Episodes list for selected show */}
                <div className="episodes-container">
                    <div className="episodes-header">
                        <span>
                            Episodes in <span id="selected-show-name">{currentShow.title || 'Select a show'}</span>
                        </span>
                        <span className="episode-count" id="episode-count">
                            {currentShow.episodes
                            ? `${currentShow.episodes.length} episode${currentShow.episodes.length !== 1 ? 's' : ''}`
                            : '0 episodes'}
                        </span>
                    </div>

                    <div className="suggested-list">
                        {!currentShow.episodes || currentShow.episodes.length === 0 ? (
                            <div className="no-content">
                                <i className="fa-solid fa-film"></i>
                                <p>Select a show to view its episodes</p>
                            </div>
                        ) : (
                            sortedEpisodes.map((episode, idx) => {
                                const originalIndex = currentShow.episodes.indexOf(episode);
                                const isActive = originalIndex === currentEpisodeIndex;
                                const duration = episode.duration || (episode.length_seconds ? formatTime(episode.length_seconds) : '');
                                
                                const episodeLink = `/podcasts/${createSlug(currentShow.title)}/${createSlug(episode.title)}`;
                                
                                return (
                                    <Link
                                        key={`episode-${idx}-${episode.title}`}
                                        to={episodeLink}
                                        className={`suggested-item ${isActive ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            loadEpisode(currentShowIndex, originalIndex, true);
                                        }}
                                    >
                                        <div className="suggested-thumb">
                                            <img 
                                                src={episode.thumbnail || episode.image || currentShow.image || CONFIG.defaultImage}
                                                alt={episode.title}
                                                onError={handleImgError}
                                            />
                                            {duration && <span className="duration-badge">{duration}</span>}
                                        </div>
                                        <div className="suggested-info">
                                            <div className="suggested-title">{episode.title}</div>
                                            <div className="suggested-meta">
                                                {episode.description ? episode.description.substring(0, 100) + '...' : ''}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default VideoCatchupPage;